<template>
  <div id="payment-form--financial-instrument-payrix">
    <v-layout row wrap>
      <v-flex xs12>
        <h2>Payment Information</h2>
      </v-flex>
    </v-layout>

    <v-layout row wrap>
      <v-flex xs12 md12>
        <InputPlaceholder
          identifier="cardName"
          display-name="Name on Card" />
      </v-flex>

      <v-flex xs12 md8>
        <InputPlaceholder
          identifier="cardNumber"
          display-name="Card Number"/>
      </v-flex>

      <v-flex xs12 md4>
        <InputPlaceholder
          identifier="securityCode"
          display-name="Security Code (CVV)" />
      </v-flex>

      <v-flex xs12 md4>
        <InputPlaceholder
          identifier="cardExpiration"
          display-name="Expiration Month/Year" />
      </v-flex>

      <v-flex xs12 md4>
        <TextInput
          v-model="localZipCode"
          v-validate="'required'"
          :validations="{ required: true }"
          display-name="Zip Code"
          identifier="zipCode"
          name="zipCode"
          data-vv-name="zipCode"
          placeholder="Zip / Postal Code"
          ref="zip-code"
          @input="$emit('update:zipCode', $event)" />
        <span
          v-show="errors.has('zipCode')"
          class="field-error">
          This field is required
        </span>
      </v-flex>

      <v-flex xs12 md6>
        <TextInput
          v-model="localEmail"
          v-validate="'required|email'"
          :validations="{ required: true, email: true }"
          display-name="Email"
          identifier="email"
          name="email"
          data-vv-name="email"
          placeholder="email@address.com"
          ref="email"
          @input="$emit('update:email', $event)" />
        <span
          v-show="errors.has('email')"
          class="field-error">
          This field is required
        </span>
      </v-flex>

      <v-flex xs12 md6>
        <TextInput
          v-model="localPhone"
          v-validate="'required'"
          :validations="{ required: true }"
          display-name="Phone Number"
          identifier="phoneNumber"
          name="phoneNumber"
          data-vv-name="phoneNumber"
          mask="###-###-####"
          placeholder="###-###-####"
          ref="phone-number"
          @input="$emit('update:phone', $event)" />
        <span
          v-show="errors.has('phoneNumber')"
          class="field-error">
          This field is required
        </span>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import InputPlaceholder from '@/components/Shared/InputPlaceholder.vue';
import TextInput from '@/components/Shared/TextInput.vue';

export default {
  components: {
    InputPlaceholder,
    TextInput,
  },
  props: {
    paymentAmount: {
      type: [Number, String],
      required: true,
    },
    merchantId: {
      type: String,
      required: true,
    },
    zipCode: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    phone: {
      type: String,
      default: '',
    },
  },
  inject: ['$validator'],
  data() {
    return {
      localZipCode: this.zipCode,
      localEmail: this.email,
      localPhone: this.phone,
    };
  },
  watch: {
    zipCode(newVal) {
      this.localZipCode = newVal;
    },
    email(newVal) {
      this.localEmail = newVal;
    },
    phone(newVal) {
      this.localPhone = newVal;
    },
  },
  methods: {
    async validateAllFields() {
      // Validate all fields individually to ensure all errors show at once
      const validations = await Promise.all([
        this.$validator.validate('zipCode'),
        this.$validator.validate('email'),
        this.$validator.validate('phoneNumber'),
      ]);
      return validations.every(result => result);
    },
    async getToken() {
      const customFieldsValid = await this.validateAllFields();

      /* eslint-disable no-undef */
      // Only pass zip, email, and phone to Payrix - no full billing address
      PayFields.config.billingAddress = {
        zip: this.localZipCode,
        email: this.localEmail,
        phone: this.localPhone,
      };
      return new Promise(resolve => {
        PayFields.onSuccess = response => {
          if (customFieldsValid) {
            resolve({
              valid: (response.data && response.data.length),
              data: response.data[0],
            });
          } else {
            resolve({
              valid: false,
              data: {},
            });
          }
        };
        PayFields.onFailure = response => {
          resolve({
            valid: false,
            data: response,
          });
        };
        PayFields.onValidationFailure = () => {
          resolve({
            valid: false,
            data: {},
          });
        };
        PayFields.submit();
      });
      /* eslint-enable no-undef */
    },
  },
  mounted() {
    const payrixScript = document.createElement('script');
    payrixScript.setAttribute('src', process.env.VUE_APP_PAYRIX_URL);
    payrixScript.onload = () => {
      /* eslint-disable no-undef */
      PayFields.config = {
        apiKey: process.env.VUE_APP_PAYRIX_APKI_KEY,
        merchant: this.merchantId,
        amount: this.paymentAmount,
        mode: 'token',
        additionalData: {},
        billingAddress: {},
      };
      PayFields.fields = [
        { type: 'name', element: '#input_cardName' },
        { type: 'number', element: '#input_cardNumber' },
        { type: 'cvv', element: '#input_securityCode' },
        { type: 'expiration', element: '#input_cardExpiration' },
      ];
      PayFields.customizations = {
        style: {
          '.input': {
            color: 'rgb(0 0 0 / 87%)',
            'font-size': '14px',
            'font-family': '"Open Sans", Helvetica, Arial, sans-serif',
            'line-height': '1.5',
            border: 'solid 1px rgba(0,0,0, 0.15)',
            'border-radius': '4px',
            padding: '1.65em 0.75em',
            'min-height': '40px',
            'box-sizing': 'border-box',
            width: '99%',
          },
          '.card-icon': {
            'padding-top': '6px',
          },
          '.form-error': {
            display: 'block',
            'padding-top': '0.4em',
            color: '#f8325a',
          },
        },
      };
      PayFields.jQuery(document)
        .ready(() => {
          PayFields.appendIframe();
        });
      /* eslint-enable no-undef */
    };
    document.head.appendChild(payrixScript);
  },
};
</script>

<style scoped>
.field-error {
  color: rgb(248, 50, 90);
  font-family: sans-serif;
  font-size: 14px;
  display: block;
  margin-top: 4px;
}
#payment-form--financial-instrument-payrix >>> label,
#payment-form--financial-instrument-payrix >>> .v-label {
  color: rgba(0, 0, 0, 0.54) !important;
}
#payment-form--financial-instrument-payrix >>> input {
  color: rgba(0, 0, 0, 0.87) !important;
}
</style>
